import { GetStaticPaths, GetStaticPropsContext } from 'next'
import { Fragment } from 'react'

import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import Metatags from '~/components/Metadata/Metatags'
import curriculumSanity from '~/shared/sanity/curriculum'
import {
  selectGradeTitle,
  setGradeContentActionCreator,
  setGradeListByCountryActionCreator,
} from '~/curriculum/slice/curriculum.slice'
import { isParamsCountryCodeAndGradeValid } from '~/curriculum/utils/validateCountryCodeGrade'
import { useAppSelector } from '~/redux/hooks'
import CurriculumGradePage from '~/curriculum/pages/CurriculumGrade.page'
import useRedirectV2UserToGradeOverview from '~/curriculum/hooks/useRedirectV2UserToGradeOverview'

export default function CurriculumOverviewPage(): React.ReactElement {
  // Redirect Curriculum v2 users off this v1 grade overview to their v2 grade overview.
  useRedirectV2UserToGradeOverview()

  const title = useAppSelector(selectGradeTitle)
  return (
    <Fragment>
      <Metatags noindex title={title} />
      <CurriculumGradePage />
    </Fragment>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ params }: GetStaticPropsContext<{ grade: string; countryCode: string }>) => {
    const countryCodeWithGradeLabel = params?.countryCode as string
    const grade = params?.grade as string

    if (!isParamsCountryCodeAndGradeValid({ countryCode: countryCodeWithGradeLabel, grade })) {
      return {
        notFound: true,
      }
    }

    const countryCode = countryCodeWithGradeLabel.split('-')[1]
    const gradeID = `grade-${countryCode === 'default' || countryCode === 'ua' ? 'us' : countryCode}-${grade}`

    const gradeContent = await curriculumSanity.getCurriculumContentList(gradeID, countryCode)
    const gradeList = await curriculumSanity.getCurriculumGradeListByCountry(countryCode, 'v1')
    store.dispatch(setGradeListByCountryActionCreator(gradeList))
    store.dispatch(setGradeContentActionCreator(gradeContent))

    return {
      props: {
        path: `/curriculum/${countryCodeWithGradeLabel}/${grade}`,
        namespaces: [...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES, 'curriculum'],
      },
      revalidate: process.env.ENVIRONMENT === 'staging' ? 60 : false,
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)
